import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socketClient } from '@/api/socket';

interface DeviceStatusEvent {
  device_id: number;
  user_id: number;
  status: 'online' | 'offline';
  platform?: string;
  model?: string;
  timestamp: string;
}

interface DeviceNewEvent extends DeviceStatusEvent {
  platform: string;
  model: string;
}

export const useRealtimeDevices = () => {
  const queryClient = useQueryClient();

  const handleDeviceNew = useCallback((event: DeviceNewEvent) => {
    console.log('🆕 [REALTIME] New device registered:', event);

    queryClient.invalidateQueries({ queryKey: ['devices'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'metrics'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'recent-devices'] });
  }, [queryClient]);

  const handleDeviceStatus = useCallback((event: DeviceStatusEvent) => {
    console.log(`${event.status === 'online' ? '🟢' : '🔴'} [REALTIME] Device status changed:`, event);

    queryClient.invalidateQueries({ queryKey: ['devices'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'metrics'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'recent-devices'] });

    queryClient.setQueryData(['device', event.device_id.toString()], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        status: event.status,
        last_seen: event.timestamp,
      };
    });
  }, [queryClient]);

  useEffect(() => {
    const socket = socketClient.getSocket();
    if (!socket) {
      console.warn('Socket not initialized for realtime devices');
      return;
    }

    socket.on('device:new', handleDeviceNew);
    socket.on('device:status', handleDeviceStatus);

    return () => {
      socket.off('device:new', handleDeviceNew);
      socket.off('device:status', handleDeviceStatus);
    };
  }, [handleDeviceNew, handleDeviceStatus]);
};
